import { useMemo, useState } from 'react';
import { ConfirmationModal, TextField } from 'seagull';
import { RichTextEditor, extractPureTextFromRichText } from 'seagull/components/RichTextEditor/RichTextEditor';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLegalTerms } from "../../../hooks/reactQuery/queries/useLegalTerms";
import styles from "./CreateDocumentModal.module.scss";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const MAX_NAME_LENGTH = 80;
const MAX_CONTENT_LENGTH = 100000;
export const {
  CreateDocumentModalTID
} = getTestIds();
export const CreateDocumentModal = props => {
  const {
    onConfirm,
    dismissModal
  } = props;
  const {
    legalTerms
  } = useLegalTerms({
    refreshOnHookMount: true
  });
  const [name, setName] = useState('');
  const [content, setContent] = useState(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const existingNames = useMemo(() => new Set(legalTerms.map(term => term.name.toLowerCase())), [legalTerms]);
  const isDuplicateName = existingNames.has(name.trim().toLowerCase());
  const isNameTooLong = name.length > MAX_NAME_LENGTH;
  const contentText = content ? extractPureTextFromRichText(content) : '';
  const isContentTooLong = contentText.length > MAX_CONTENT_LENGTH;
  const isContentEmpty = !contentText.trim();
  const isNameEmpty = !name.trim();
  const hasNameError = isDuplicateName || isNameTooLong;
  const hasContentError = isContentTooLong;
  const hasError = hasNameError || hasContentError;
  const nameHelperText = useMemo(() => {
    if (isDuplicateName) {
      return 'Document name already exists. Please use a different one.';
    } else if (isNameTooLong) {
      return 'Document name can be up to 80 characters';
    }
  }, [isDuplicateName, isNameTooLong]);
  const contentHelperText = isContentTooLong ? 'Document content can be up to 100,000 characters' : undefined;
  const handleCreate = async () => {
    if (!hasError && name.trim()) {
      setIsSaving(true);
      const created = await onConfirm(name.trim(), content);
      if (created) {
        dismissModal(created);
      }
      setIsSaving(false);
    }
  };
  return /*#__PURE__*/_jsx(ConfirmationModal, {
    title: "Create document",
    width: "800px",
    height: "fit-content",
    "data-testid": CreateDocumentModalTID.Root(),
    textAlignCenter: false,
    content: /*#__PURE__*/_jsxs("div", {
      className: styles.contentWrapper,
      children: [/*#__PURE__*/_jsx(TextField, {
        collapseHelperText: true,
        testid: CreateDocumentModalTID.NameInput(),
        label: "Document name",
        value: name,
        onChange: e => setName(e.target.value),
        error: hasNameError,
        helperText: nameHelperText,
        helperTextReserveSpace: true,
        width: "100%",
        autoFocus: true
      }), /*#__PURE__*/_jsx(RichTextEditor, {
        "data-testid": CreateDocumentModalTID.ContentEditor(),
        editorHeight: 300,
        error: hasContentError,
        helperText: contentHelperText,
        onChange: value => setContent(value)
      })]
    }),
    primaryButtonLabel: "Create",
    secondaryButtonLabel: "Cancel",
    primaryButtonDisabled: hasError || isNameEmpty || isContentEmpty,
    primaryButtonLoading: isSaving,
    onPrimaryButtonClick: handleCreate,
    onSecondaryButtonClick: () => dismissModal()
  });
};